import React from 'react';

import { ButtonType as InventoryCTAButtonType } from '../block/InventoryCTA.jsx';

import { Block, withBlockRendering } from '../common/Block.jsx';
import { Clickable } from '../common/Button.jsx';
import { default as _Image } from '../common/Image.jsx';
import LocalStorage from '../common/LocalStorage.jsx';
import Name from '../common/Name.jsx';
import PageType from '../common/PageType.jsx';
import { BackgroundOverlay } from '../common/Popup.jsx';
import useOwnerRealm from '../common/useOwnerRealm.jsx';
import useScrollLock from '../common/useScrollLock.jsx';

import { EditorContainerWrapper, PageBottomHiddenDraggable, PageTopHiddenDraggable } from '../layout/Builder.jsx';
import Defaults from '../layout/Defaults.jsx';
import Kit from '../layout/Kit.jsx';

// An artwork counts as viewed once it has been on screen this long.
const ARTWORK_VIEW_DWELL_MS = 2000;

function _InventoryModal(props) {
    // Resolve doc/window off the rendered node so it works inside the iframe too.
    const { getDocument, getWindow, rootRef } = useOwnerRealm();

    useScrollLock(!props.builder || props.preview, getDocument);

    // Current Inventory Data
    const data = props.data || [];
    const [activeIndex, setActiveIndex] = React.useState(undefined);
    const [cachedPageConfig, setCachedPageConfig] = React.useState(undefined);
    const handleUpdateActiveIndex = function (delta) {
        setActiveIndex(undefined);
        const index = (activeIndex + data.length + delta) % data.length;
        setTimeout(() => setActiveIndex(index), 10);
    }
    const handleNext = () => handleUpdateActiveIndex(1);
    const handlePrevious = () => handleUpdateActiveIndex(-1);
    React.useEffect(() => {
        for (let i = 0; i < data.length; i++) {
            if (data[i].id != props.inventoryId) {
                continue;
            }
            setActiveIndex(i);
            break;
        }
    }, []);
    React.useEffect(() => {
        if (props.builder || props.popupVisible || activeIndex === undefined || data.length <= 1) {
            return;
        }
        const handleKeyDown = (e) => {
            switch (e.key) {
                case 'ArrowLeft':
                    handlePrevious();
                    break;
                case 'ArrowRight':
                    handleNext();
                    break;
            }
        };
        const win = getWindow();
        win.addEventListener('keydown', handleKeyDown);
        return () => win.removeEventListener('keydown', handleKeyDown);
    }, [activeIndex, data.length, props.builder, props.popupVisible]);
    
    let isPresentation = props.isPresentation;
    let inventory;
    if (props.builder) {
        inventory = props.data && props.data.inventory;
        isPresentation = props.isPresentation || props.parentBlock && props.parentBlock.type && props.parentBlock.type.value == Name.Block.Inventory.Presentation.List;
    } else if (activeIndex < data.length) {
        inventory = data[activeIndex];
    }
    React.useEffect(() => {
        if (props.builder || isPresentation || !inventory) {
            return;
        }
        LocalStorage.RecentlyViewed.put(inventory);
    }, [inventory]);
    React.useEffect(() => {
        if (props.builder || props.preview || !isPresentation || !inventory) {
            return;
        }
        const doc = getDocument();
        let counted = false;
        let remaining = ARTWORK_VIEW_DWELL_MS;
        let startedAt, timer;
        const pause = () => {
            if (timer == undefined) {
                return;
            }
            clearTimeout(timer);
            timer = undefined;
            remaining -= Date.now() - startedAt;
        };
        const resume = () => {
            // `counted` keeps a later visibility change from re-arming an already counted view.
            if (counted || timer != undefined || doc.hidden) {
                return;
            }
            startedAt = Date.now();
            timer = setTimeout(() => {
                timer = undefined;
                counted = true;
                if (window && window.gtag) {
                    gtag('event', 'artwork_view', {
                        'inventory_id': inventory.id
                    });
                }
            }, remaining);
        };
        const handleVisibilityChange = () => doc.hidden ? pause() : resume();
        resume();
        doc.addEventListener('visibilitychange', handleVisibilityChange);
        return () => {
            pause();
            doc.removeEventListener('visibilitychange', handleVisibilityChange);
        };
    }, [inventory?.id]);

    // Content Properties
    let className = 'ol__cont ol--active ol--aw modal cont--whitelabel';
    if (props.builder) {
        className += ' builder';
    }
    const contentProps = {
        className,
        id: props.id,
        onDragOver: (e) => { if (e) { e.preventDefault() } },
        onDrop: props.onDrop
    }
    let pageConfig = props.contentConfig || props.pageConfig || cachedPageConfig;
    if (!props.builder && !pageConfig) {
        pageConfig = Defaults.PageConfig[props.isPresentation ? PageType.Content.Presentation.InventoryDetailModal : PageType.InventoryDetailModal](props.config);

        // Cache the default page config so the block id's don't change on re-renders
        setCachedPageConfig(pageConfig);
    }
    let containerProps;
    if (props.onPageProps) {
        containerProps = props.onPageProps();
    } else if (pageConfig.desktop) {
        containerProps = pageConfig.desktop.page;
    }

    // Block Config Style Parsing
    const arrowLeftStyle = {};
    const arrowRightStyle = {};
    if (props.builder) {
        arrowLeftStyle.pointerEvents = 'none';
        arrowLeftStyle.position = 'fixed';
        arrowRightStyle.pointerEvents = 'none';
        arrowRightStyle.position = 'fixed';
    }
    const style = { height: '100%', overflow: 'auto' }
    let scrollable, topHiddenOffset;
    if (containerProps && containerProps.config) {
        let configProps;
        for (let configName in containerProps.config) {
            configProps = containerProps.config[configName];
            Block.mapStyleConfigProps(style, configProps, (propName, propValue, propUnit) => {
                switch (propName) {
                    case 'arrowColor':
                        arrowLeftStyle.color = propValue;
                        arrowRightStyle.color = propValue;
                        break;
                    case 'arrowOffset': {
                        if (!propValue || !propUnit) {
                            return true;
                        }
                        const value = `${propValue}${propUnit}`;
                        arrowLeftStyle.left = value;
                        arrowRightStyle.right = value;
                        return true;
                    }
                    case 'arrowSize': {
                        if (!propValue || !propUnit) {
                            return true;
                        }
                        const value = `${propValue}${propUnit}`;
                        arrowLeftStyle.fontSize = value;
                        arrowRightStyle.fontSize = value;
                        return true;
                    }
                    case 'paddingTop':
                        topHiddenOffset = propValue;
                        break;
                    case 'scrollable':
                        scrollable = propValue;
                        return true;
                }
            });
        }
    }

    // Block Rendering
    let blocks = [];
    if (pageConfig.desktop && pageConfig.desktop.blocks) {
        blocks = pageConfig.desktop.blocks;
    }
    let backgroundColor = style.backgroundColor;
    if (!backgroundColor) {
        switch (props.pageType) {
            case PageType.Content.Presentation.InventoryDetailModal:
                const presentationKit = Kit.presentationKit(props.config, PageType.Custom);
                backgroundColor = presentationKit && presentationKit.backgroundColor;
                break;
            default:
                backgroundColor = props.config && props.config.colors && props.config.colors.bodyBackground;
                break;
        }
    }
    let renderedBlocks = props.onBlockRendering(
        props.pageType,
        blocks,
        (d) => {
            if (!d.type) {
                return;
            }
            const blockProps = { backgroundColor };
            switch (d.type.value) {
                case Name.Block.CloseButton:
                    blockProps.onClick = props.onClose;
                    break;
                case Name.Block.Inventory.Button.InquireV1:
                case Name.Block.Inventory.Button.InquireV2:
                case Name.Block.Inventory.Button.InquireV3:
                case Name.Block.Inventory.Button.OfferV1:
                case Name.Block.Inventory.Button.OfferV2:
                case Name.Block.Inventory.Button.OfferV3:
                case Name.Block.Inventory.Button.PurchaseV1:
                case Name.Block.Inventory.Button.PurchaseV2:
                case Name.Block.Inventory.Button.PurchaseV3:
                    const buttonType = InventoryCTAButtonType.fromBlockConfig(d);
                    switch (buttonType) {
                        case InventoryCTAButtonType.Inquire:
                            blockProps.onButtonClick = () => props.onInquire(inventory);
                            break;
                        case InventoryCTAButtonType.Offer:
                            blockProps.onButtonClick = () => props.onOffer(inventory);
                            break;
                        case InventoryCTAButtonType.Purchase:
                            blockProps.hidden = isPresentation;
                            blockProps.onButtonClick = () => props.onPurchase(inventory);
                            break;
                    }
                    blockProps.isPresentation = isPresentation;
                    break;
                case Name.Block.Inventory.Image:
                    blockProps.useVH = true;
                    break;
                case Name.Block.Inventory.RecentlyViewed.List:
                    blockProps.editor = false;
                    break;
                case Name.Block.ModalButtonNext:
                    blockProps.onButtonClick = handleNext;
                    blockProps.isPresentation = isPresentation;
                    break;
                case Name.Block.ModalButtonPrevious:
                    blockProps.onButtonClick = handlePrevious;
                    blockProps.isPresentation = isPresentation;
                    break;
            }
            return blockProps;
        },
        (d, blockConfig, parentProps) => {
            let blockProps;
            if (props.builderBlockPropsHandler) {
                blockProps = props.builderBlockPropsHandler(d, blockConfig, parentProps)
            }
            if (!d.type) {
                return blockProps;
            }
            if (!blockProps) {
                blockProps = {};
            }
            if (!blockProps.style) {
                blockProps.style = {};
            }
            switch (d.type.value) {
                case Name.Block.Columns:
                    if (blockConfig && blockConfig.layout) {
                        if (blockConfig.layout.position == 'fixed') {
                            let bottom;
                            if (blockConfig.layout.bottom && blockConfig.layout.bottom.value && blockConfig.layout.bottom.value.length > 0 && blockConfig.layout.bottom.unit) {
                                bottom = `${blockConfig.layout.bottom.value}${blockConfig.layout.bottom.unit}`;
                            }
                            let top;
                            if (blockConfig.layout.top && blockConfig.layout.top.value && blockConfig.layout.top.value.length > 0 && blockConfig.layout.top.unit) {
                                top = `${blockConfig.layout.top.value}${blockConfig.layout.top.unit}`;
                            }
                            let zIndex;
                            switch (blockConfig.layout.zIndex) {
                                case '-1':
                                    zIndex = '0';
                                    break;
                                default:
                                    zIndex = '2';
                            }
                            blockProps.style.bottom = bottom;
                            blockProps.style.position = 'fixed';
                            blockProps.style.top = top;
                            blockProps.style.width = 'calc(100% - 8px)';
                            blockProps.style.zIndex = zIndex;
                        }
                    }
                    break;
                case Name.Block.Inventory.Image:
                    blockProps.style.zIndex = '1';
                    break;
                default:
                    blockProps.style.zIndex = '1';
                    break;
            }
            return blockProps;
        },
        { inventory }
    );

    let content = renderedBlocks;
    if (!scrollable) {
        let height = '100%';
        if (props.builder) {
            style.height = 'calc(100vh - 34px)';
            height = 'calc(100% + 32px)';
        } else {
            style.height = '100vh';
        }
        style.overflow = 'hidden';
        style.width = props.editor ? 'calc(100vw - 8px)' : '100vw';
        content = <div style={{ display: 'flex', flexDirection: 'column', height }}>{content}</div>
    }
    content = (
        <React.Fragment>
            <BackgroundOverlay />
            <div style={style}>
                {
                    props.editor &&
                    <PageTopHiddenDraggable offset={topHiddenOffset} pageEmpty={!renderedBlocks || renderedBlocks.length == 0} onDragEnter={props.onDragEnter} />
                }
                <Clickable className="ol__arrow ol__prev" style={arrowLeftStyle} onClick={handlePrevious}><span className="i i-arrow-left" /></Clickable>
                <Clickable className="ol__arrow ol__next" style={arrowRightStyle} onClick={handleNext}><span className="i i-arrow-right" /></Clickable>
                {inventory != undefined && content}
                {
                    props.editor &&
                    <PageBottomHiddenDraggable onDragEnter={props.onDragEnter} />
                }
            </div>
        </React.Fragment>
    );
    if (props.editor) {
        return <EditorContainerWrapper {...contentProps}>{content}</EditorContainerWrapper>
    }
    return <div {...contentProps} ref={rootRef}>{content}</div>
}
export default withBlockRendering(_InventoryModal);